'use client'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Plus,
  Users,
  Settings,
  Trash2,
  UserPlus,
  UserMinus,
} from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { useTeamsStore } from '@/stores/teams'
import { useAgentAuth } from '@/stores/auth'
import { useToastNotificationStore } from '@/lib/toast-notifications'
import { api } from '@/lib/api'
import type { Team, TeamWithMembers } from '@/types'

interface TeamConfigurationPanelProps {
  className?: string
}

export function TeamConfigurationPanel({
  className = '',
}: TeamConfigurationPanelProps) {
  const { user } = useAgentAuth()
  const {
    teams,
    isLoadingTeams,
    fetchTeams,
    createTeam,
    updateTeam,
    deleteTeam,
  } = useTeamsStore()
  const { addNotification, removeNotification } = useToastNotificationStore()

  const [selectedTeam, setSelectedTeam] = useState<TeamWithMembers | null>(null)
  const [isCreatingTeam, setIsCreatingTeam] = useState(false)
  const [isEditingTeam, setIsEditingTeam] = useState(false)
  const [isManagingMembers, setIsManagingMembers] = useState(false)
  const [availableAgents, setAvailableAgents] = useState<any[]>([])

  // Form states
  const [teamName, setTeamName] = useState('')
  const [teamDescription, setTeamDescription] = useState('')
  const [teamIsActive, setTeamIsActive] = useState(true)
  const [selectedAgents, setSelectedAgents] = useState<string[]>([])

  useEffect(() => {
    fetchTeams()
  }, [fetchTeams])

  const loadAvailableAgents = useCallback(async () => {
    try {
      const response = await api.admin.listAgents()

      if (!response.success || !Array.isArray(response.data)) {
        throw new Error(response.error || response.message || 'Request failed')
      }

      setAvailableAgents(response.data)
    } catch (error) {
      console.error('Failed to load agents:', error)
      setAvailableAgents([])
      const errorMessage =
        error instanceof Error && error.message
          ? error.message
          : 'Failed to load available agents'
      addNotification({
        id: 'load-agents-error',
        type: 'error',
        message: errorMessage,
        duration: 3000,
      })
    }
  }, [addNotification])

  useEffect(() => {
    if (isManagingMembers) {
      loadAvailableAgents()
    }
  }, [isManagingMembers, loadAvailableAgents])

  const handleCreateTeam = async () => {
    if (!teamName.trim()) {
      addNotification({
        id: 'create-team-validation',
        type: 'error',
        message: 'Team name is required',
        duration: 3000,
      })
      return
    }

    try {
      const result = await createTeam({
        name: teamName.trim(),
        description: teamDescription.trim(),
        isActive: teamIsActive,
      })

      if (result.success) {
        addNotification({
          id: 'create-team-success',
          type: 'success',
          message: 'Team created successfully',
          duration: 3000,
        })
        setIsCreatingTeam(false)
        resetTeamForm()
        fetchTeams()
      } else {
        addNotification({
          id: 'create-team-error',
          type: 'error',
          message: result.error || 'Failed to create team',
          duration: 3000,
        })
      }
    } catch (error) {
      console.error('Create team error:', error)
      addNotification({
        id: 'create-team-error',
        type: 'error',
        message: 'An unexpected error occurred',
        duration: 3000,
      })
    }
  }

  const handleUpdateTeam = async () => {
    if (!selectedTeam || !teamName.trim()) return

    try {
      const result = await updateTeam(selectedTeam.id, {
        name: teamName.trim(),
        description: teamDescription.trim(),
        isActive: teamIsActive,
      })

      if (result.success) {
        addNotification({
          id: 'update-team-success',
          type: 'success',
          message: 'Team updated successfully',
          duration: 3000,
        })
        setIsEditingTeam(false)
        setSelectedTeam(null)
        resetTeamForm()
        fetchTeams()
      } else {
        addNotification({
          id: 'update-team-error',
          type: 'error',
          message: result.error || 'Failed to update team',
          duration: 3000,
        })
      }
    } catch (error) {
      console.error('Update team error:', error)
      addNotification({
        id: 'update-team-error',
        type: 'error',
        message: 'An unexpected error occurred',
        duration: 3000,
      })
    }
  }

  const handleDeleteTeam = async () => {
    if (!selectedTeam) return

    try {
      const result = await deleteTeam(selectedTeam.id)

      if (result.success) {
        addNotification({
          id: 'delete-team-success',
          type: 'success',
          message: 'Team deleted successfully',
          duration: 3000,
        })
        setSelectedTeam(null)
        fetchTeams()
      } else {
        addNotification({
          id: 'delete-team-error',
          type: 'error',
          message: result.error || 'Failed to delete team',
          duration: 3000,
        })
      }
    } catch (error) {
      console.error('Delete team error:', error)
      addNotification({
        id: 'delete-team-error',
        type: 'error',
        message: 'An unexpected error occurred',
        duration: 3000,
      })
    }
  }

  const handleManageTeamMembers = async () => {
    if (!selectedTeam || selectedAgents.length === 0) return

    try {
      const addPromises = selectedAgents.map(agentId =>
        api.admin.addAgentToTeam(selectedTeam.id, parseInt(agentId))
      )

      await Promise.all(addPromises)

      addNotification({
        id: 'manage-members-success',
        type: 'success',
        message: 'Team members updated successfully',
        duration: 3000,
      })

      setIsManagingMembers(false)
      setSelectedAgents([])
      fetchTeams()
      // Refresh selected team to get updated members
      const updatedTeams = await api.admin.getTeams()
      if (updatedTeams.success) {
        const updatedTeam = updatedTeams.data?.find(
          (t: TeamWithMembers) => t.id === selectedTeam.id
        )
        if (updatedTeam) {
          setSelectedTeam(updatedTeam)
        }
      }
    } catch (error) {
      console.error('Manage team members error:', error)
      addNotification({
        id: 'manage-members-error',
        type: 'error',
        message: 'Failed to update team members',
        duration: 3000,
      })
    }
  }

  const handleRemoveTeamMember = async (teamId: number, agentId: number) => {
    try {
      const result = await api.admin.removeAgentFromTeam(teamId, agentId)

      if (result.success) {
        addNotification({
          id: 'remove-member-success',
          type: 'success',
          message: 'Team member removed successfully',
          duration: 3000,
        })

        // Refresh selected team
        const updatedTeams = await api.admin.getTeams()
        if (updatedTeams.success) {
          const updatedTeam = updatedTeams.data?.find(
            (t: TeamWithMembers) => t.id === teamId
          )
          if (updatedTeam) {
            setSelectedTeam(updatedTeam)
          }
        }

        fetchTeams()
      } else {
        addNotification({
          id: 'remove-member-error',
          type: 'error',
          message: result.error || 'Failed to remove team member',
          duration: 3000,
        })
      }
    } catch (error) {
      console.error('Remove team member error:', error)
      addNotification({
        id: 'remove-member-error',
        type: 'error',
        message: 'An unexpected error occurred',
        duration: 3000,
      })
    }
  }

  const resetTeamForm = () => {
    setTeamName('')
    setTeamDescription('')
    setTeamIsActive(true)
    setSelectedAgents([])
  }

  const startEditTeam = (team: TeamWithMembers) => {
    setSelectedTeam(team)
    setTeamName(team.name)
    setTeamDescription(team.description || '')
    setTeamIsActive(team.isActive)
    setIsEditingTeam(true)
  }

  if (isLoadingTeams) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-500">Loading teams...</p>
        </div>
      </div>
    )
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header Actions */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            Team Management
          </h3>
          <p className="text-sm text-gray-600">
            Manage your teams and their members
          </p>
        </div>
        <Button
          onClick={() => setIsCreatingTeam(true)}
          className="flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Create Team
        </Button>
      </div>

      {/* Teams Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {teams.map((team: TeamWithMembers) => (
          <Card key={team.id} className="hover:shadow-md transition-shadow">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-blue-600" />
                  <CardTitle className="text-base">{team.name}</CardTitle>
                </div>
                <Badge variant={team.isActive ? 'default' : 'secondary'}>
                  {team.isActive ? 'Active' : 'Inactive'}
                </Badge>
              </div>
              {team.description && (
                <CardDescription className="text-sm">
                  {team.description}
                </CardDescription>
              )}
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1 text-sm text-gray-500">
                  <Users className="h-4 w-4" />
                  <span>{team.members?.length || 0} members</span>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => startEditTeam(team)}
                    className="h-8 w-8 p-0"
                  >
                    <Settings className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSelectedTeam(team)
                      setIsManagingMembers(true)
                    }}
                    className="h-8 w-8 p-0"
                  >
                    <UserPlus className="h-4 w-4" />
                  </Button>
                  {team.members && team.members.length > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSelectedTeam(team)
                        handleDeleteTeam()
                      }}
                      className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Selected Team Details */}
      {selectedTeam && !isEditingTeam && !isManagingMembers && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-600" />
              {selectedTeam.name}
              <Badge variant={selectedTeam.isActive ? 'default' : 'secondary'}>
                {selectedTeam.isActive ? 'Active' : 'Inactive'}
              </Badge>
            </CardTitle>
            {selectedTeam.description && (
              <CardDescription>{selectedTeam.description}</CardDescription>
            )}
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Team Members</h4>
                {selectedTeam.members && selectedTeam.members.length > 0 ? (
                  <div className="space-y-2">
                    {selectedTeam.members.map(member => (
                      <div
                        key={member.id}
                        className="flex items-center justify-between p-2 bg-gray-50 rounded"
                      >
                        <div>
                          <div className="font-medium text-gray-900">
                            {member.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {member.email}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            handleRemoveTeamMember(selectedTeam.id, member.id)
                          }
                          className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <UserMinus className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500">No members in this team</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create Team Dialog */}
      <Dialog open={isCreatingTeam} onOpenChange={setIsCreatingTeam}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Team</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="team-name">Team Name *</Label>
              <Input
                id="team-name"
                value={teamName}
                onChange={e => setTeamName(e.target.value)}
                placeholder="Enter team name"
              />
            </div>
            <div>
              <Label htmlFor="team-description">Description</Label>
              <Textarea
                id="team-description"
                value={teamDescription}
                onChange={e => setTeamDescription(e.target.value)}
                placeholder="Enter team description"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsCreatingTeam(false)
                resetTeamForm()
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleCreateTeam}>Create Team</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Team Dialog */}
      <Dialog open={isEditingTeam} onOpenChange={setIsEditingTeam}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Team</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-team-name">Team Name *</Label>
              <Input
                id="edit-team-name"
                value={teamName}
                onChange={e => setTeamName(e.target.value)}
                placeholder="Enter team name"
              />
            </div>
            <div>
              <Label htmlFor="edit-team-description">Description</Label>
              <Textarea
                id="edit-team-description"
                value={teamDescription}
                onChange={e => setTeamDescription(e.target.value)}
                placeholder="Enter team description"
                rows={3}
              />
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="edit-team-active"
                checked={teamIsActive}
                onChange={e => setTeamIsActive(e.target.checked)}
                className="rounded border-gray-300"
              />
              <Label htmlFor="edit-team-active">Team is active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsEditingTeam(false)
                setSelectedTeam(null)
                resetTeamForm()
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleUpdateTeam}>Update Team</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage Members Dialog */}
      <Dialog open={isManagingMembers} onOpenChange={setIsManagingMembers}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Manage Team Members - {selectedTeam?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="agent-select">Add Agents to Team</Label>
              <Select
                onValueChange={value => {
                  if (value && !selectedAgents.includes(value)) {
                    setSelectedAgents([...selectedAgents, value])
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select agents to add" />
                </SelectTrigger>
                <SelectContent>
                  {availableAgents
                    .filter(
                      agent =>
                        !selectedTeam?.members?.some(
                          member => member.id === agent.id
                        ) && !selectedAgents.includes(agent.id.toString())
                    )
                    .map(agent => (
                      <SelectItem key={agent.id} value={agent.id.toString()}>
                        {agent.name} ({agent.email})
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            {selectedAgents.length > 0 && (
              <div>
                <Label>Agents to Add:</Label>
                <div className="space-y-2 mt-2">
                  {selectedAgents.map(agentId => {
                    const agent = availableAgents.find(
                      a => a.id.toString() === agentId
                    )
                    return agent ? (
                      <div
                        key={agentId}
                        className="flex items-center justify-between p-2 bg-gray-50 rounded"
                      >
                        <div>
                          <div className="font-medium">{agent.name}</div>
                          <div className="text-sm text-gray-500">
                            {agent.email}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            setSelectedAgents(
                              selectedAgents.filter(id => id !== agentId)
                            )
                          }
                          className="h-8 w-8 p-0"
                        >
                          ×
                        </Button>
                      </div>
                    ) : null
                  })}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsManagingMembers(false)
                setSelectedAgents([])
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleManageTeamMembers}
              disabled={selectedAgents.length === 0}
            >
              Add Members
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
